import React, { useCallback, useEffect, useRef, useState } from 'react';
import useShiftGroupsService from '../../services/useShiftGroupService';
import { ShiftGroup } from '../../models/ShiftGroupModel';
import showToast from '../../utils/toast';
import ShiftGroupForm from '../../components/ShiftGroups/ShiftGroupForm';
import ButtonCancelForm from '../../components/forms/ButtonCancelForm';
import ButtonSaveForm from '../../components/forms/ButtonSaveForm';
import ConfirmAlert from '../../components/Base/ConfirmAlert';
import ShiftGroupsTable from './ShiftGroupTable';
import { useFormErrors } from '../../hooks/useFormErrors';
import Pagination from '../../components/Base/Pagination';
import TableTextSearch from '../../components/Base/TableTextSearch';
import MaintainerPageShell from '../../components/Base/MaintainerPageShell';
import { Users2, Plus } from 'lucide-react';

const ShiftGroupsPage: React.FC = () => {
  const svc = useShiftGroupsService();
  const { list, store, update, remove, fetching } = svc;
  const [shiftGroups, setShiftGroups] = useState<ShiftGroup[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
  });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ShiftGroup | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [savingLocked, setSavingLocked] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [shiftGroupIdToDelete, setShiftGroupIdToDelete] = useState<number | null>(null);
  const activeRequest = useRef<AbortController | null>(null);
  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();
  const [searchText, setSearchText] = useState('');
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback((page: number = 1, search?: string) => {
    activeRequest.current?.abort();
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    const filters: Record<string, unknown> = { page };
    const term = (search !== undefined ? search : searchTextRef.current).trim();
    if (term) filters.search = term;

    list(filters as Record<string, string | number>, {
      onSuccess: (resp: any) => {
        const data = resp?.data ?? {};
        setShiftGroups(data.shift_groups ?? []);

        if (data.pagination) {
          setPagination({
            page: data.pagination.page || page,
            totalPages: data.pagination.total_pages || 1,
            totalCount: data.pagination.total_count || 0,
            pageSize: data.pagination.page_size || 10,
            hasNext: data.pagination.has_next || false,
            hasPrevious: data.pagination.has_previous || false,
          });
        }
      },
      onError: () => showToast('Error cargando grupos', 'error'),
    }, ctrl.signal);
  }, [list]);

  useEffect(() => {
    load();
    return () => {
      activeRequest.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [load]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => load(1, value), 400);
    },
    [load]
  );

  const openCreate = () => {
    setEditing({ name: '' });
    setFieldError(null);
    setShowForm(true);
  };

  const openEdit = (s: ShiftGroup) => {
    setEditing(s);
    setFieldError(null);
    setShowForm(true);
  };

  const callbackSave = async (): Promise<any> => {
    if (!editing) return;

    setFieldError(null);
    setSavingLocked(true);
    clearErrors();

    try {
      if (editing.id) {
        const r = await update(editing.id, { name: editing.name });
        return r;
      } else {
        const r = await store({ name: editing.name });
        return r;
      }
    } catch (error: any) {
      const nameErr =
        error?.errors?.items?.[0]?.name?.[0] ??
        error?.errors?.items?.['0']?.name?.[0];

      if (nameErr) {
        setErrorsFromResponse({ name: [nameErr] });
      }

      throw error;
    } finally {
      setSavingLocked(false);
    }
  };

  const handleSaveComplete = (ok: boolean, resp?: any) => {
    if (ok) {
      showToast(resp?.message ?? 'Guardado', 'success');
      setShowForm(false);
      setEditing(null);
      clearErrors();
      load(pagination.page, searchTextRef.current.trim() || undefined);
      return;
    }

    setErrorsFromResponse(resp);
    showToast(resp?.message ?? 'Error guardando', 'error');
  };

  const handleClose = () => {
    clearErrors();
    setShowForm(false);
    setFieldError(null);
  };

  const openDeleteConfirmation = (id: number) => {
    setShiftGroupIdToDelete(id);
    setIsAlertOpen(true);
  };

  const handleConfirmDelete = () => {
    if (shiftGroupIdToDelete === null) return;

    remove(shiftGroupIdToDelete, {
      onSuccess: () => {
        showToast('Eliminado', 'success');
        load(pagination.page, searchTextRef.current.trim() || undefined);
      },
      onError: () => showToast('Error eliminando', 'error'),
    });

    setIsAlertOpen(false);
    setShiftGroupIdToDelete(null);
  };

  const totalLabel = fetching.list
    ? 'Cargando...'
    : (
      <span className="maintainer-page__meta-count">
        <span className="maintainer-page__meta-pill">
          {pagination.totalCount || shiftGroups.length}
        </span>
        grupos
      </span>
    );

  return (
    <MaintainerPageShell
      title="Grupos"
      icon={<Users2 size={22} strokeWidth={2.25} />}
      meta={totalLabel}
      actions={
        <button type="button" className="btn-create" onClick={openCreate}>
          <Plus size={16} strokeWidth={2.25} aria-hidden />
          Nuevo grupo
        </button>
      }
    >
      <ShiftGroupsTable
        data={shiftGroups}
        loading={fetching.list}
        onEdit={openEdit}
        onDelete={openDeleteConfirmation}
        totalCount={pagination.totalCount}
        searchTerm={searchText}
        onClearSearch={() => handleSearchChange('')}
        toolbar={
          <TableTextSearch
            value={searchText}
            onChange={handleSearchChange}
            placeholder="Buscar por nombre de grupo..."
            aria-label="Buscar grupos por nombre"
          />
        }
        footer={
          pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              onPageChange={(page) => {
                load(page, searchTextRef.current.trim() || undefined);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          ) : null
        }
      />

      {showForm && editing && (
        <div className="shifts-modal">
          <div className="shifts-modal-inner">
            <h3>{editing.id ? 'Editar grupo' : 'Crear grupo'}</h3>
            <ShiftGroupForm value={editing} onChange={setEditing} error={fieldError || undefined} fieldErrors={fieldErrors} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <ButtonCancelForm callbackCancel={handleClose} locked={savingLocked} confirmCancel={false} />
              <ButtonSaveForm callbackSave={callbackSave} locked={savingLocked} onComplete={handleSaveComplete} />
            </div>
          </div>
        </div>
      )}

      <ConfirmAlert
        isOpen={isAlertOpen}
        title="¿Eliminar grupo?"
        message="Esta acción es permanente y no se puede deshacer."
        onConfirm={handleConfirmDelete}
        onClose={() => setIsAlertOpen(false)}
      />
    </MaintainerPageShell>
  );
};

export default ShiftGroupsPage;
